/* eslint-disable react/prop-types */
/**
 * VoiceDictation — Iter 248.1.
 *
 * Small mic button you can drop next to ANY textarea / input to add
 * voice → text dictation. Same Whisper pipeline the chat composer
 * uses; the transcript is returned via `onText(transcript)` so the
 * caller decides what to do (append, replace, submit, etc.).
 *
 * Why this exists: every text input the rep types into for more than
 * 10 words should support voice (Trust Contract — handsfree everywhere
 * we ask the rep to author content). The original chat composer ties
 * voice to a "send message" action; this is the plain dictation
 * primitive for free-form textareas (e.g. report refine feedback,
 * BoM manual lines, ticket notes, etc.).
 *
 * Usage:
 *   const [text, setText] = useState("");
 *   <textarea value={text} onChange={(e) => setText(e.target.value)} />
 *   <VoiceDictation
 *     onText={(t) => setText((prev) => (prev ? prev + " " : "") + t)}
 *     disabled={busy}
 *     size="sm"
 *   />
 */
import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "./ui/sonner";

const MIN_VOICE_BLOB_BYTES = 1500;
const VOICE_CAP_MS = 5 * 60 * 1000; // 5 min cap for dictation (chat is 15)

export default function VoiceDictation({
  onText,
  onGracefulFail,
  onStateChange,
  disabled = false,
  size = "sm",
  testId = "voice-dictation",
}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [recordedSec, setRecordedSec] = useState(0);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const capTimerRef = useRef(null);
  const startedAtRef = useRef(0);

  // Iter 427.p22 — surface busy state to parents so they can freeze
  // dependent send buttons while we're still recording OR waiting
  // for Whisper. This is the p14 voice-race guarantee: no downstream
  // action fires before the transcript has been applied.
  //
  // Iter 427.p57.g (2026-02-24) — pin onStateChange in a ref so the
  // effect does NOT re-fire just because the parent passes a new
  // inline arrow every render. Parents typically write
  // `onStateChange={(s) => setBusy(s.busy)}` which is a fresh identity
  // per render — without this ref, every parent re-render would
  // re-invoke the callback with the SAME state and set a new object
  // in the parent (e.g. `{ ...prev, [id]: false }`), which never
  // bails out and produces "Maximum update depth exceeded".
  const onStateChangeRef = useRef(onStateChange);
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  });
  useEffect(() => {
    onStateChangeRef.current?.({ recording, transcribing, busy: recording || transcribing });
  }, [recording, transcribing]);

  // Tidy up timers if the component unmounts mid-recording.
  useEffect(() => () => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (capTimerRef.current) clearTimeout(capTimerRef.current);
    try {
      if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
        mediaRecRef.current.stop();
      }
    } catch { /* ignore */ }
  }, []);

  const _transcribe = async (blob) => {
    setTranscribing(true);
    try {
      const ext = (blob.type || "").includes("mp4") ? "m4a" : "webm";
      const file = new File([blob], `dictation-${Date.now()}.${ext}`, { type: blob.type });
      const fd = new FormData();
      fd.append("audio", file);
      const { data } = await api.post("/hidro/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      });
      const transcript = (data?.text || "").trim();
      if (transcript) {
        onText?.(transcript);
      } else {
        // [Iter 285] In addition to the toast, surface the graceful
        // failure to the caller so they can render a persistent
        // inline banner. The brief toast on its own gets missed
        // when the rep is mid-record and watching the mic icon.
        const msg = data?.user_message || "Couldn't catch that — try again, closer to the mic.";
        toast.message(msg);
        onGracefulFail?.(msg);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Transcription failed.";
      toast.error(msg);
      onGracefulFail?.(msg);
    } finally {
      setTranscribing(false);
    }
  };

  const stopRecording = ({ commit = true } = {}) => {
    try {
      if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
        mediaRecRef.current._commit = commit;
        mediaRecRef.current.stop();
      }
    } catch { /* ignore */ }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (capTimerRef.current) { clearTimeout(capTimerRef.current); capTimerRef.current = null; }
    setRecording(false);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Voice dictation not supported on this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = "";
      for (const t of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"]) {
        if (window.MediaRecorder.isTypeSupported(t)) { mimeType = t; break; }
      }
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (rec._commit === false) return;
        if (blob.size < MIN_VOICE_BLOB_BYTES) {
          toast.error("Recording too short — tap the mic, speak, then tap again to stop.");
          return;
        }
        await _transcribe(blob);
      };
      rec.start();
      mediaRecRef.current = rec;
      startedAtRef.current = Date.now();
      setRecording(true);
      setRecordedSec(0);
      tickRef.current = setInterval(() => {
        setRecordedSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
      capTimerRef.current = setTimeout(() => {
        toast.info("Recording hit the 5-minute cap — transcribing now.");
        stopRecording({ commit: true });
      }, VOICE_CAP_MS);
    } catch (e) {
      if (e?.name === "NotAllowedError") {
        toast.error("Microphone access denied. Allow it in your browser settings.");
      } else {
        toast.error(e?.message || "Could not start recording.");
      }
    }
  };

  const onTap = () => {
    if (disabled || transcribing) return;
    if (recording) {
      stopRecording({ commit: true });
    } else {
      startRecording();
    }
  };

  const sizeCls = size === "lg" ? "w-9 h-9" : "w-7 h-7";
  const iconCls = size === "lg" ? "w-4 h-4" : "w-3.5 h-3.5";

  if (transcribing) {
    return (
      <button
        type="button"
        disabled
        className={`${sizeCls} inline-flex items-center justify-center rounded-md border border-cyan-400/40 bg-cyan-500/10 text-cyan-200`}
        data-testid={`${testId}-transcribing`}
        title="Transcribing…"
      >
        <Loader2 className={`${iconCls} animate-spin`} />
      </button>
    );
  }

  if (recording) {
    return (
      <button
        type="button"
        onClick={onTap}
        className={`${sizeCls} inline-flex items-center justify-center rounded-md border border-rose-400/60 bg-rose-500/20 text-rose-100 animate-pulse`}
        data-testid={`${testId}-stop`}
        title={`Recording · ${recordedSec}s · tap to stop & transcribe`}
      >
        <Square className={iconCls} />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onTap}
      disabled={disabled}
      className={`${sizeCls} inline-flex items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-[#cbd5e1] hover:border-cyan-400/40 hover:text-cyan-200 disabled:opacity-40 disabled:cursor-not-allowed`}
      data-testid={`${testId}-start`}
      title="Dictate with your voice"
    >
      <Mic className={iconCls} />
    </button>
  );
}
